import {
  Flag,
  IdCard,
  Lock,
  Mail,
  MapPin,
  Phone,
  Settings2,
  User,
  Users
} from "lucide-react";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { AssigneesMenu } from "../../caseIde/header/chips/AssigneesChip";
import { RowLabel, RowValueButton } from "../../caseIde/header/chips/chip";
import { AssignedUsersAvatarStack } from "../../foreignNationals/components/AssignedUsersAvatarStack";
import { AvatarUpload } from "../components/AvatarUpload";
import { useClientProfile } from "../context/ClientProfileContext";
import { formatShortDate } from "./clientHeaderModel";
import type { ClientDetails } from "./useClientDetails";

const Row: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex h-8 items-center gap-1">{children}</div>
);

const Placeholder: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="text-muted-foreground italic">{children}</span>
);

const Icon: FC<{ icon: typeof User }> = ({ icon: I }) => (
  <I className="size-3.5 text-fg-3" />
);

const PhoneRow: FC<{
  value: string;
  onSubmit: (value: string) => Promise<void>;
}> = ({ value, onSubmit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [editing, value]);
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    if (next === value.trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(next);
      setEditing(false);
    } catch {
      // toast raised by the hook; stay in edit mode
    } finally {
      setSaving(false);
    }
  };

  return (
    <Row>
      <RowLabel>
        <Icon icon={Phone} /> Phone
      </RowLabel>
      {editing ? (
        <Input
          ref={inputRef}
          autoFocus
          type="tel"
          value={draft}
          disabled={saving}
          placeholder="Phone number"
          className="h-7 flex-1 text-xs"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(value);
              setEditing(false);
            }
          }}
        />
      ) : (
        <RowValueButton
          onClick={() => setEditing(true)}
          title={value || undefined}
          aria-label="Edit phone number"
        >
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <Placeholder>Add phone</Placeholder>
          )}
        </RowValueButton>
      )}
    </Row>
  );
};

/** Popover body listing the client's key properties. */
export const ClientDetailsBody: FC<{ details: ClientDetails }> = ({
  details
}) => {
  const { url, openPersonalSection } = useClientProfile();
  const assignedNames = details.teamMembers
    .filter((m) => details.assignedUserIds.includes(m.uid))
    .map((m) => m.label);
  const expiration = formatShortDate(details.statusExpiration);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2.5 pb-2">
        <AvatarUpload
          imageUrl={details.avatarUrl}
          initials={details.initials}
          isUploading={details.isUploadingAvatar}
          onUpload={details.uploadAvatar}
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">
            {details.name || "Unknown client"}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {details.status || "No immigration status"}
            {details.status && expiration ? ` · expires ${expiration}` : ""}
          </span>
        </div>
      </div>

      <div className="h-px bg-border" />

      <Row>
        <RowLabel>
          <Icon icon={IdCard} /> Status
        </RowLabel>
        <RowValueButton
          onClick={() => url.setTab("immigration_overview")}
          title="Open immigration status"
        >
          {details.status ? (
            <span
              className={cn(
                "truncate",
                details.statusUrgency === "expired" && "text-danger",
                details.statusUrgency === "soon" && "text-warn"
              )}
            >
              {details.status}
            </span>
          ) : (
            <Placeholder>Add status</Placeholder>
          )}
        </RowValueButton>
      </Row>

      <Row>
        <RowLabel>
          <Icon icon={Flag} /> Citizenship
        </RowLabel>
        <RowValueButton
          onClick={() =>
            openPersonalSection("personal-immigration-information")
          }
        >
          {details.citizenship ? (
            <span className="truncate">{details.citizenship}</span>
          ) : (
            <Placeholder>Add citizenship</Placeholder>
          )}
        </RowValueButton>
      </Row>

      <Row>
        <RowLabel>
          <Icon icon={Mail} /> Email
        </RowLabel>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="flex h-7 min-w-0 flex-1 items-center gap-1.5 px-1.5 text-xs"
              aria-label="Primary email (locked)"
            >
              {details.email ? (
                <span className="truncate">{details.email}</span>
              ) : (
                <Placeholder>No email</Placeholder>
              )}
              <Lock className="size-3 shrink-0 text-fg-4" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Primary email cannot be changed
          </TooltipContent>
        </Tooltip>
      </Row>

      <PhoneRow value={details.phone} onSubmit={details.setPhone} />

      <Row>
        <RowLabel>
          <Icon icon={MapPin} /> Address
        </RowLabel>
        <RowValueButton
          onClick={() => openPersonalSection("personal-current-address")}
          title={details.addressLine || undefined}
        >
          {details.addressLine ? (
            <span className="truncate">{details.addressLine}</span>
          ) : (
            <Placeholder>Add address</Placeholder>
          )}
        </RowValueButton>
      </Row>

      <Row>
        <RowLabel>
          <Icon icon={Users} /> Assignees
        </RowLabel>
        <AssigneesMenu
          assigned={details.assignedUserIds}
          team={details.teamMembers}
          isLoading={details.isLoadingTeamMembers}
          isSaving={details.isSavingAssignees}
          onChange={details.setAssignees}
          align="start"
        >
          <DropdownMenuTrigger asChild>
            <RowValueButton
              disabled={details.isSavingAssignees}
              title={
                assignedNames.length > 0 ? assignedNames.join(", ") : undefined
              }
            >
              {details.assignedUserIds.length > 0 ? (
                <span className="flex items-center [&_[aria-label]>div]:size-5! [&_[aria-label]_img]:size-5! [&_[aria-label]_span]:text-xs!">
                  <AssignedUsersAvatarStack
                    userIds={details.assignedUserIds}
                    maxVisible={4}
                    nonInteractive
                  />
                </span>
              ) : (
                <Placeholder>Assign team members</Placeholder>
              )}
            </RowValueButton>
          </DropdownMenuTrigger>
        </AssigneesMenu>
      </Row>

      <div className="mt-1 h-px bg-border" />

      <div className="flex items-center justify-center gap-1 pt-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 flex-1 justify-center text-xs"
          onClick={() => url.setTab("personal")}
        >
          <User className="size-3.5" /> Personal Information
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 flex-1 justify-center text-xs"
          onClick={() => url.setTab("settings")}
        >
          <Settings2 className="size-3.5" /> Settings
        </Button>
      </div>
    </div>
  );
};

/** Popover wrapper; `children` is the trigger element. */
export const ClientDetailsPopover: FC<{
  details: ClientDetails;
  children: ReactNode;
}> = ({ details, children }) => (
  <Popover>
    <PopoverTrigger asChild>{children}</PopoverTrigger>
    <PopoverContent align="start" sideOffset={6} className="w-85 p-3">
      <ClientDetailsBody details={details} />
    </PopoverContent>
  </Popover>
);
